import React, { Fragment, useState, useEffect } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { APP_NAVIGATION } from "../../utils/constants";
import PathHelper from "../../utils/path-helper";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { currentDomain } from "../../utils/general-config";

const ShareIntroModal = ({ showShareModal, toggleShareModal, toggleShareWithOthersModal }) => {
  const { locale } = useParams();
  const { t } = useTranslation();
  const domain = currentDomain().toLowerCase();
  const [privacyPolicy, setPrivacyPolicy] = useState("");
  const [termsOfUse, setTermsOfUse] = useState("");

  const setUrls = (privacyPolicy, termsOfUse) => {
    setPrivacyPolicy(privacyPolicy);
    setTermsOfUse(termsOfUse);
  };

  let privacyLinksOption = PathHelper(APP_NAVIGATION.PRVY, false);
  let termsAndConditionOption = PathHelper(APP_NAVIGATION.TUSE, false);

  useEffect(() => {
    setUrls(privacyLinksOption.cstm, termsAndConditionOption.cstm);
  }, [locale, domain]);

  const startSharingHanlder = () => {
    toggleShareWithOthersModal(true);
    toggleShareModal(false);
  };
  return (
    <div className="sharesimpli-wrap">
      <Transition.Root show={showShareModal} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={startSharingHanlder}>
          {/* Overlay */}
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            {/* <div className="fixed inset-0 bg-black/30" /> */}
            <div className="fixed inset-0">
              <div className="absolute inset-0 bg-[linear-gradient(0deg,_#FFF_10.95%,_rgba(255,255,255,0)_90.38%),_linear-gradient(166deg,_#FFE1CC_-68.53%,_#FFCCF7_-5.43%,_#D2F4FF_49.41%)]" />
              <div className="absolute inset-0 bg-black/10" />
            </div>
          </Transition.Child>

          <div className="fixed inset-0 flex items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="relative w-full max-w-4xl rounded-[14px] bg-white px-4 md:px-12 py-2 md:py-8 shadow-xl max-h-[99dvh] overflow-y-auto md:max-h-none md:overflow-visible border-2 border-[#FFE1CC]">
                <button
                  onClick={startSharingHanlder}
                  id="close_btn_id"
                  className="absolute right-6 top-6 focus-visible:outline-none focus-visible:ring-0"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="9" height="9" viewBox="0 0 9 9" fill="none">
                    <path
                      d="M0.75 0.75L8.25 8.25M8.25 0.75L0.75 8.25"
                      stroke="#687585"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                    />
                  </svg>
                </button>

                {/* Title */}
                <h2 className="text-center px-[20px] md:px-0 text-lg md:text-[36px] font-semibold font-rethink text-[#16283D] leading-normal">
                  Share your files in <br className="block md:hidden" />
                  three simple steps
                </h2>

                {/* Steps */}
                <div className="md:mt-6 flex flex-col md:flex-row items-center md:items-start justify-between gap-2 md:gap-0">
                  {/* Step 1 */}
                  <div className="flex flex-col items-center text-center w-full md:w-[220px]">
                    <div className="flex h-12 w-12 md:h-28 md:w-28 items-center justify-center rounded-[16px] md:rounded-[30px] bg-blue-50">
                      <img
                        className="w-6 md:w-auto"
                        src={process.env.PUBLIC_URL + "/static/images/share-upload.svg"}
                        alt=""
                      />
                    </div>
                    <h3 className="md:mt-4 font-semibold font-rethink">Upload</h3>
                    <p className="mt-1 text-sm text-[#16283D] font-rethink">
                      Securely upload <br className="hidden md:block" /> any file type
                    </p>
                  </div>

                  {/* Separator */}
                  <div className="flex w-[3px] md:w-[100px] pt-0 md:pt-[60px] justify-center">
                    <img
                      src={
                        window.innerWidth < 768
                          ? process.env.PUBLIC_URL + "/static/images/seprator-mob.svg"
                          : process.env.PUBLIC_URL + "/static/images/seprator.svg"
                      }
                      alt=""
                      className="w-[120px]"
                    />
                  </div>

                  {/* Step 2 */}
                  <div className="flex flex-col items-center text-center w-full md:w-[220px]">
                    <div className="flex h-12 w-12 md:h-32 md:w-32 items-center justify-center rounded-[16px] md:rounded-[30px] bg-blue-50">
                      <img
                        className="w-6 md:w-auto"
                        src={process.env.PUBLIC_URL + "/static/images/share-link.svg"}
                        alt=""
                      />
                    </div>
                    <h3 className="md:mt-4 font-semibold font-rethink">Share</h3>
                    <p className="mt-1 text-sm text-[#16283D] font-rethink">
                      Share using a <br className="hidden md:block" /> private link or email
                    </p>
                  </div>

                  {/* Separator */}
                  <div className="flex w-[3px] md:w-[100px] pt-0 md:pt-[60px] justify-center">
                    <img
                      src={
                        window.innerWidth < 768
                          ? process.env.PUBLIC_URL + "/static/images/seprator-mob.svg"
                          : process.env.PUBLIC_URL + "/static/images/seprator.svg"
                      }
                      alt=""
                      className="w-[120px]"
                    />
                  </div>

                  {/* Step 3 */}
                  <div className="flex flex-col items-center text-center w-full md:w-[220px]">
                    <div className="flex h-12 w-12 md:h-28 md:w-28 items-center justify-center rounded-[16px] md:rounded-[30px] bg-blue-50">
                      <img
                        className="w-6 md:w-auto"
                        src={process.env.PUBLIC_URL + "/static/images/share-users.svg"}
                        alt=""
                      />
                    </div>
                    <h3 className="md:mt-4 font-semibold font-rethink">Recipient access</h3>
                    <p className="mt-1 text-sm text-[#16283D] font-rethink">
                      The recipient can access your file instantly—no sign-up needed
                    </p>
                  </div>
                </div>

                <div className="mt-2 md:mt-10 flex justify-center">
                  <button
                    className="flex items-center gap-3 rounded-full bg-gradient-to-r from-orange-500 to-orange-400 px-10 py-3 font-semibold font-rethink text-white shadow hover:opacity-90"
                    onClick={startSharingHanlder}
                    id="start_sharing_btn_id"
                  >
                    START SHARING
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="12" viewBox="0 0 20 10" fill="none">
                      <path
                        d="M15.25 1L19 4.75M19 4.75L15.25 8.5M19 4.75H1"
                        stroke="white"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                </div>

                {/* Footer */}
                <p className="mt-4 text-center text-xs text-[#264A73] font-rethink">
                  By continuing, you confirm that you have read and consent to our <br className="hidden md:block" />
                  <a className="" href={termsOfUse} target="_blank" id="terms_of_use_id">
                    {" "}
                    <span className="text-[#0E76FF] cursor-pointer">Terms of Use</span>
                  </a>
                  and{" "}
                  <a className="" href={privacyPolicy} target="_blank" id="privacy_policy_id">
                    {" "}
                    <span className="text-[#0E76FF] cursor-pointer">Privacy Policy</span>
                  </a>
                </p>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition.Root>
    </div>
  );
};

export default ShareIntroModal;
